import React from 'react';

const SmsOutboundDetailsModal = ({ sms, onClose }) => {
	const formatDate = (dateString) => {
		if (!dateString) return '-';
		return dateString;
	};

	const getStatusBadge = (status) => {
		const badges = {
			pending: 'bg-warning',
			sent: 'bg-success',
			sent_digest: 'bg-info',
			failed: 'bg-danger',
		};
		const labels = {
			pending: 'Pending',
			sent: 'Sent',
			sent_digest: 'Digest',
			failed: 'Failed',
		};

		return (
			<span className={`badge ${badges[status] || 'bg-secondary'}`}>
				{labels[status] || status}
			</span>
		);
	};

	return (
		<>
			{/* Modal Backdrop */}
			<div
				className="modal-backdrop fade show"
				onClick={onClose}
				style={{ zIndex: 1040 }}
			/>

			{/* Modal */}
			<div
				className="modal fade show d-block"
				tabIndex="-1"
				style={{ zIndex: 1050 }}
			>
				<div className="modal-dialog modal-lg modal-dialog-scrollable">
					<div className="modal-content">
						{/* Header */}
						<div className="modal-header">
							<h5 className="modal-title">
								<i className="bi bi-send me-2" />
								Outbound SMS Alert Details
							</h5>
							<button type="button" className="btn-close" onClick={onClose} />
						</div>

						{/* Body */}
						<div className="modal-body">
							{/* Status Section */}
							<div className="mb-4">
								<h6 className="border-bottom pb-2 mb-3">Delivery Status</h6>
								<div className="row g-3">
									<div className="col-md-6">
										<label className="text-muted small">Status</label>
										<div>{getStatusBadge(sms.status)}</div>
									</div>
									<div className="col-md-6">
										<label className="text-muted small">Tier</label>
										<div>
											<span className="badge bg-info">{sms.tier}</span>
										</div>
									</div>
									<div className="col-md-6">
										<label className="text-muted small">Template Key</label>
										<div>
											<code>{sms.template_key}</code>
										</div>
									</div>
									<div className="col-md-6">
										<label className="text-muted small">Twilio SID</label>
										<div>
											<code>{sms.twilio_sid || '—'}</code>
										</div>
									</div>
									<div className="col-md-6">
										<label className="text-muted small">Created At</label>
										<div>{formatDate(sms.created_at)}</div>
									</div>
									{sms.sent_at && (
										<div className="col-md-6">
											<label className="text-muted small">Sent At</label>
											<div className="text-success">
												<i className="bi bi-check-circle me-1" />
												{formatDate(sms.sent_at)}
											</div>
										</div>
									)}
								</div>
							</div>

							{/* Recipient Section */}
							<div className="mb-4">
								<h6 className="border-bottom pb-2 mb-3">
									Subscriber Information
								</h6>
								{sms.user ? (
									<div className="row g-3">
										<div className="col-md-6">
											<label className="text-muted small">Name</label>
											<div className="fw-semibold">{sms.user.name}</div>
										</div>
										<div className="col-md-6">
											<label className="text-muted small">Phone</label>
											<div>{sms.user.phone || '—'}</div>
										</div>
										<div className="col-md-6">
											<label className="text-muted small">Email</label>
											<div>{sms.user.email}</div>
										</div>
										<div className="col-md-6">
											<label className="text-muted small">User ID</label>
											<div>#{sms.user.id}</div>
										</div>
									</div>
								) : (
									<div className="text-muted">
										No user record attached (User ID #{sms.user_id}).
									</div>
								)}
							</div>

							{/* Outbound Message Body Section */}
							<div className="mb-4">
								<h6 className="border-bottom pb-2 mb-3">
									<i className="bi bi-chat-left-text me-2" />
									Outbound Message Content
								</h6>
								{sms.body ? (
									<div className="p-3 bg-light rounded border">
										<pre
											className="mb-0 text-dark"
											style={{
												fontSize: '0.9rem',
												whiteSpace: 'pre-wrap',
												fontFamily: 'inherit',
											}}
										>
											{sms.body}
										</pre>
									</div>
								) : (
									<div className="text-muted">
										<em>Message body could not be rendered.</em>
									</div>
								)}
							</div>

							{/* Property Section */}
							{(sms.property || sms.property_id) && (
								<div className="mb-4">
									<h6 className="border-bottom pb-2 mb-3">Related Property</h6>
									<div className="row g-3">
										{sms.property && (
											<div className="col-md-8">
												<label className="text-muted small">Address</label>
												<div>
													<i className="bi bi-building me-2" />
													{sms.property.address}
												</div>
											</div>
										)}
										<div className="col-md-4">
											<label className="text-muted small">Property ID</label>
											<div>#{sms.property_id}</div>
										</div>
										{sms.item_id && (
											<div className="col-md-4">
												<label className="text-muted small">Item ID</label>
												<div>#{sms.item_id}</div>
											</div>
										)}
										{sms.class && (
											<div className="col-md-8">
												<label className="text-muted small">
													Dataset Class
												</label>
												<div>
													<code className="text-secondary">{sms.class}</code>
												</div>
											</div>
										)}
									</div>
								</div>
							)}

							{/* Error Section */}
							{sms.error_message && (
								<div className="mb-4">
									<h6 className="border-bottom pb-2 mb-3 text-danger">
										<i className="bi bi-exclamation-triangle me-2" />
										Error Details
									</h6>
									<div className="alert alert-danger mb-0">
										<code>{sms.error_message}</code>
									</div>
								</div>
							)}

							{/* Raw Template Fields */}
							{sms.fields && Object.keys(sms.fields).length > 0 && (
								<div className="mb-4">
									<h6 className="border-bottom pb-2 mb-3">Template Fields</h6>
									<pre
										className="mb-0 p-3 bg-light rounded"
										style={{ fontSize: '0.85rem' }}
									>
										{JSON.stringify(sms.fields, null, 2)}
									</pre>
								</div>
							)}

							{/* Technical Details */}
							<div className="mb-0">
								<h6 className="border-bottom pb-2 mb-3">Technical Details</h6>
								<div className="row g-3">
									<div className="col-md-4">
										<label className="text-muted small">Queued SMS ID</label>
										<div>
											<code>#{sms.id}</code>
										</div>
									</div>
									<div className="col-md-4">
										<label className="text-muted small">User ID</label>
										<div>
											<code>#{sms.user_id}</code>
										</div>
									</div>
									<div className="col-md-4">
										<label className="text-muted small">Property ID</label>
										<div>
											<code>#{sms.property_id}</code>
										</div>
									</div>
								</div>
							</div>
						</div>

						{/* Footer */}
						<div className="modal-footer">
							<button
								type="button"
								className="btn btn-secondary"
								onClick={onClose}
							>
								Close
							</button>
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default SmsOutboundDetailsModal;
